import { useEffect, useRef } from "react";
import { useSinkState, useSinkActions } from "../context/sink";

export default function useScrollPositionRestoration(
  scrollContainerRef: React.RefObject<HTMLElement>,
  isDataReady: boolean = true // defaults to true for backward compatibility
) {
  const { surveyListScrollPosition } = useSinkState();
  const { setSurveyListScrollPosition } = useSinkActions();
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasRestoredRef = useRef(false);
  const initialPositionRef = useRef<number | null>(null);

  // Capture initial position on first render
  if (initialPositionRef.current === null) {
    initialPositionRef.current = surveyListScrollPosition;
  }

  // Restore scroll position after content height stabilizes
  useEffect(() => {
    if (!isDataReady || !scrollContainerRef.current || hasRestoredRef.current) {
      return;
    }

    const scrollContainer = scrollContainerRef.current;
    const savedPosition = initialPositionRef.current;

    if (savedPosition === null || savedPosition <= 0) {
      // No position to restore, mark as restored so save handler can work
      hasRestoredRef.current = true;
      return;
    }

    const restoreScrollPosition = () => {
      if (!scrollContainer || hasRestoredRef.current) {
        return;
      }

      const maxScroll =
        scrollContainer.scrollHeight - scrollContainer.clientHeight;
      let targetPosition = savedPosition;

      // If saved position is beyond current scrollHeight, restore to max possible
      if (savedPosition > maxScroll) {
        targetPosition = Math.max(0, maxScroll);
      }

      scrollContainer.scrollTop = targetPosition;
      hasRestoredRef.current = true;
    };

    let lastScrollHeight = scrollContainer.scrollHeight;
    let stableFrames = 0;
    let rafId: number;

    const checkStability = () => {
      if (hasRestoredRef.current) return;

      const currentHeight = scrollContainer.scrollHeight;

      if (currentHeight === lastScrollHeight) {
        stableFrames++;
        // Wait for 2 stable frames before restoring
        if (stableFrames >= 2) {
          restoreScrollPosition();
          return;
        }
      } else {
        // Height changed, reset counter
        stableFrames = 0;
        lastScrollHeight = currentHeight;
      }

      rafId = requestAnimationFrame(checkStability);
    };

    // Start checking after first frame
    rafId = requestAnimationFrame(checkStability);

    // Cleanup
    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [isDataReady, scrollContainerRef]);

  // Save scroll position on scroll (debounced)
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }

    const handleScroll = () => {
      // Don't save if we're still waiting to restore
      // Only save if restoration is complete OR there was nothing to restore
      if (!hasRestoredRef.current && surveyListScrollPosition > 0) {
        return;
      }

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        setSurveyListScrollPosition(scrollContainer.scrollTop);
      }, 100);
    };

    scrollContainer.addEventListener("scroll", handleScroll);

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [scrollContainerRef, setSurveyListScrollPosition]);
}
